import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Link } from "react-router-dom";
import { ContactCTA } from "../sections/ContactCTA";
import { Reveal } from "../components/Reveal";
import { useSeo } from "../hooks/useSeo";
import { LEISTUNGEN_DETAIL, PROCESS, FAQ, IMG, CONTACT, INSTAGRAM } from "../data/content";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Check, Phone, ArrowRight } from "lucide-react";

const LeistungenHero = () => (
  <section data-testid="leistungen-hero" className="relative min-h-[68vh] flex items-end overflow-hidden">
    <img src={IMG.leistungenHeader} alt="Eichendielen und Werkzeug – handwerkliche Verlegung" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B30]/95 via-[#2B2B30]/55 to-[#2B2B30]/30" />
    <div className="container-blum relative z-10 pb-16 pt-40">
      <Reveal>
        <p className="font-script text-3xl text-[#D7C4B1]">Alles aus einer Hand</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mt-3 max-w-3xl">
          Unsere Leistungen
        </h1>
        <p className="text-lg text-white/85 mt-5 max-w-2xl leading-relaxed">
          Vom ersten Beratungsgespräch bis zur fertigen Fläche – handwerkliche Sorgfalt
          in jedem Schritt. Und das seit über 55 Jahren.
        </p>
      </Reveal>
    </div>
  </section>
);

const Process = () => (
  <section data-testid="process-section" className="py-20 lg:py-32 bg-white border-y border-[#E5E0D8]">
    <div className="container-blum">
      <div className="max-w-2xl mb-14">
        <Reveal><p className="overline">So arbeiten wir</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4">Ihr Weg zum neuen Boden</h2>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
        {PROCESS.map((p, i) => (
          <Reveal key={p.n} delay={i * 0.06}>
            <div data-testid={`process-step-${i}`} className="relative p-6 bg-[#F9F8F6] border border-[#E5E0D8] h-full">
              <span className="font-display text-4xl font-bold text-[#D7C4B1]">{p.n}</span>
              <h3 className="font-display text-lg font-semibold text-[#2B2B30] mt-3">{p.title}</h3>
              <p className="text-[#6B6B73] text-sm mt-2 leading-relaxed">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Detail = () => (
  <section data-testid="leistungen-detail" className="py-20 lg:py-32">
    <div className="container-blum space-y-20 lg:space-y-28">
      {LEISTUNGEN_DETAIL.map((d, i) => (
        <div key={d.title} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
          <Reveal>
            <div className="img-zoom relative">
              <img src={d.img} alt={d.title} className="w-full aspect-[5/4] object-cover" />
            </div>
          </Reveal>
          <div>
            <Reveal><p className="overline">Leistung {String(i + 1).padStart(2, "0")}</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">{d.title}</h2>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="font-display text-lg lg:text-xl font-semibold text-[#D32F2F] mt-4">{d.lead}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[#6B6B73] text-lg leading-relaxed mt-3">{d.text}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <ul className="mt-7 space-y-3">
                {d.points.map((pt) => (
                  <li key={pt} className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center bg-[#D32F2F] shrink-0">
                      <Check className="w-3.5 h-3.5 text-white" />
                    </span>
                    <span className="text-[#232327] font-medium">{pt}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const Faq = () => (
  <section data-testid="faq-section" className="py-20 lg:py-32 bg-white border-y border-[#E5E0D8]">
    <div className="container-blum grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <Reveal><p className="overline">Beratung</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">Häufige Fragen rund um Ihren neuen Boden</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-[#6B6B73] mt-5 leading-relaxed">
            Noch Fragen offen? Rufen Sie uns an – wir beraten Sie gern persönlich.
          </p>
          <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 mt-5 font-display font-semibold text-[#D32F2F]">
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
        </Reveal>
      </div>
      <div className="lg:col-span-8">
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-[#E5E0D8]">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="font-display text-lg text-[#2B2B30] hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#6B6B73] text-base leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
        <Reveal delay={0.15}>
          <div data-testid="faq-outro" className="mt-8 p-7 bg-[#F9F8F6] border border-[#E5E0D8]">
            <h3 className="font-display text-xl font-semibold text-[#2B2B30]">Ihre Frage war nicht dabei?</h3>
            <p className="text-[#6B6B73] leading-relaxed mt-3">
              Jeder Raum und jeder Boden ist anders. Rufen Sie uns gerne an oder besuchen Sie uns in
              unserer Ausstellung – viele Fragen lassen sich in einem persönlichen Gespräch am
              schnellsten klären.
            </p>
            <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 mt-4 font-display font-semibold text-[#D32F2F]">
              <Phone className="w-4 h-4" /> {CONTACT.phone}
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ReferenzenTeaser = () => (
  <section data-testid="referenzen-teaser" className="py-20 lg:py-28 bg-white border-y border-[#E5E0D8]">
    <div className="container-blum">
      <div className="relative overflow-hidden">
        <img src={IMG.refHero} alt="Abgeschlossene Projekte von Fussboden Blum" className="w-full aspect-[21/9] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2B2B30]/90 via-[#2B2B30]/60 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="p-8 lg:p-14 max-w-xl">
            <Reveal>
              <p className="overline text-[#D7C4B1]">Referenzen</p>
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-white mt-4 leading-tight">
                Echte Projekte. Sichtbarer Unterschied.
              </h2>
              <p className="text-white/85 mt-4 leading-relaxed">
                Werfen Sie einen Blick auf eine beispielhafte Auswahl abgeschlossener Projekte –
                von Parkett bis Akustiklösung.
              </p>
              <a href={INSTAGRAM.url} target="_blank" rel="noopener noreferrer" data-testid="referenzen-teaser-link" className="btn-base btn-red mt-7">
                Referenzen sehen (zur Instagram-Seite) <ArrowRight className="w-4 h-4" />
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default function Leistungen() {
  useSeo(
    "Leistungen von Fussboden Blum - Unser Qualitätsversprechen",
    "Leistungen von Fussboden Blum - Unser Qualitätsversprechen an unsere Kunden. Lesen Sie hier mehr dazu."
  );
  return (
    <div data-testid="leistungen-page">
      <Navbar />
      <main>
        <LeistungenHero />
        <Process />
        <Detail />
        <Faq />
        <ReferenzenTeaser />
        <ContactCTA hideForm />
      </main>
      <Footer />
    </div>
  );
}
